import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { TrendingUp, Shield, Users, Zap } from "lucide-react";

const steps = [
  {
    icon: Users,
    title: "Sign Up",
    description: "Create your verified MuskConnect Pro account with Telegram, PayPal, or crypto wallet.",
  },
  {
    icon: Shield,
    title: "Verify Identity",
    description: "Complete KYC verification for security and compliance.",
  },
  {
    icon: TrendingUp,
    title: "Invest",
    description: "Choose your investment tier ($100–$10,000) and funding method.",
  },
  {
    icon: Zap,
    title: "Earn Rewards",
    description: "Receive Bitcoin rewards quarterly based on platform growth and milestones.",
  },
];

const tiers = [
  { name: "Starter", min: "$100", max: "$499", reward: "0.5% quarterly", icon: "⭐" },
  { name: "Pro", min: "$500", max: "$2,999", reward: "1.2% quarterly", icon: "🌟" },
  { name: "Elite", min: "$3,000", max: "$10,000", reward: "2.0% quarterly + exclusive access", icon: "✨" },
];

export default function InvestmentPortal() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const tierVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
    hover: {
      y: -8,
      transition: { duration: 0.3 },
    },
  };
  return (
    <motion.div
      className="space-y-16"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      {/* Disclaimer */}
      <motion.div variants={itemVariants} className="border border-border p-8">
        <p className="text-sm text-foreground font-medium mb-2">Important Disclaimer</p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          This investment program is educational and for informational purposes only. It is not financial advice.
          All investments carry risk. Please conduct thorough research and consult with a financial advisor.
        </p>
      </motion.div>

      {/* Steps */}
      <motion.div variants={itemVariants}>
        <h3 className="text-3xl font-medium mb-12">How It Works</h3>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                variants={itemVariants}
                whileHover={{ y: -5 }}
                className="border border-border p-8 flex flex-col items-center text-center cursor-pointer hover:border-foreground transition-colors"
              >
                <Icon className="h-8 w-8 text-foreground mb-6" />
                <h4 className="text-base font-medium text-foreground mb-3">{step.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </motion.div>

      {/* Investment Tiers */}
      <motion.div variants={itemVariants}>
        <h3 className="text-3xl font-medium mb-12">Investment Tiers</h3>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {tiers.map((tier, idx) => (
            <motion.div
              key={tier.name}
              variants={tierVariants}
              whileHover="hover"
              className={`border p-8 flex flex-col h-full cursor-pointer transition-all ${
                idx === 1 ? "border-foreground bg-card" : "border-border hover:border-foreground/50"
              }`}
            >
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
                {tier.name}
              </p>
              <p className="text-2xl font-medium text-foreground mb-2">{tier.min}</p>
              <p className="text-sm text-muted-foreground mb-6">to {tier.max}</p>
              <div className="border-t border-border my-6" />
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">
                Quarterly Return
              </p>
              <p className="text-lg font-medium text-foreground mb-auto">{tier.reward}</p>
              <Button
                className={`mt-8 w-full font-medium ${
                  idx === 1
                    ? "bg-foreground text-background hover:bg-foreground/90"
                    : "border border-foreground text-foreground hover:bg-foreground hover:text-background"
                }`}
              >
                Select
              </Button>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Key Benefits */}
      <motion.div variants={itemVariants}>
        <h3 className="text-3xl font-medium mb-12">Benefits</h3>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {[
            "Exclusive community access and events",
            "Direct updates from project leaders",
            "Early initiative announcements",
            "Quarterly Bitcoin rewards",
            "Portfolio diversification",
            "Transparent analytics dashboard",
          ].map((benefit) => (
            <motion.div
              key={benefit}
              variants={itemVariants}
              className="flex items-start gap-3 hover:translate-x-2 transition-transform"
            >
              <Zap className="h-4 w-4 text-foreground flex-shrink-0 mt-1" />
              <p className="text-foreground text-sm">{benefit}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}